<template>
    <main class="relative flex flex-col w-full h-full min-h-svh">
        <header class="fixed top-0 left-0 flex items-center w-full py-4 border-b border-ewhite-200 bg-white/80 backdrop-blur-md z-[999999]">
            <div class="flex container justify-between items-center mx-auto px-4 md:px-6">
                <div class="flex items-center">
                    <div class="flex items-center gap-2">
                        <img src="@/assets/Mantaraya.png" :alt="$t('marketing.auto.emanta_logo')" class="h-12 w-auto object-contain">
                        <h1 class="text-2xl font-bold tracking-tight text-eblack-900 hidden md:block font-modulus-semi-bold">{{ $t("marketing.auto.emanta") }}</h1>
                    </div>
                </div>

                <div class="flex flex-row items-center gap-4 font-modulus">
                    <div class="flex items-center md:order-2 my-1 space-x-1 md:space-x-0 rtl:space-x-reverse">
                        <button type="button" data-dropdown-toggle="language-dropdown-menu" class="inline-flex gap-1.5 items-center font-semibold justify-center px-4 py-2 text-sm text-eblack-800 hover:text-eorange-500 transition-colors duration-300 cursor-pointer">
                           <svg class="w-5 h-5 text-eblack-800 hover:text-eorange-500 transition-colors duration-300" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m13 19 3.5-9 3.5 9m-6.125-2h5.25M3 7h7m0 0h2m-2 0c0 1.63-.793 3.926-2.239 5.655M7.5 6.818V5m.261 7.655C6.79 13.82 5.521 14.725 4 15m3.761-2.345L5 10m2.761 2.655L10.2 15"/>
                            </svg>
                            {{ language_selected }}
                        </button>
                        <div class="z-50 hidden my-4 text-base list-none border border-ewhite-200 bg-white divide-y divide-ewhite-100 rounded-xl shadow-lg" id="language-dropdown-menu">
                            <ul class="py-2 font-medium" role="none">
                                <li>
                                    <button
                                    type="button"
                                    class="block w-full text-left px-4 py-2.5 text-sm text-eblack-700 hover:bg-ewhite-50 transition-colors duration-200"
                                    role="menuitem"
                                    @click="changeLanguage('en')">
                                        <div class="inline-flex items-center">{{ $t("marketing.auto.english_us") }}</div>
                                    </button>
                                </li>
                                <li>
                                    <button
                                    type="button"
                                    class="block w-full text-left px-4 py-2.5 text-sm text-eblack-700 hover:bg-ewhite-50 transition-colors duration-200"
                                    role="menuitem"
                                    @click="changeLanguage('es')">
                                        <div class="inline-flex items-center">{{ $t("marketing.auto.spanish_esp") }}</div>
                                    </button>
                                </li>
                            </ul>
                        </div>
                    </div>
                    <button
                    type="button"
                    class="text-sm hidden md:block rounded-full px-5 py-2.5 border border-eorange-500 text-eorange-500 hover:bg-eorange-500 hover:text-white font-semibold transition-all duration-300">
                        {{ $t('common.contact') }}
                    </button>
                    <a
                    href="#cta-section"
                    class="text-sm hidden md:block rounded-full px-5 py-2.5 border border-eorange-500 bg-eorange-500 text-white hover:bg-eorange-600 hover:border-eorange-600 hover:scale-[1.02] hover:shadow-lg hover:shadow-eorange-500/20 font-semibold transition-all duration-300">
                        {{ $t('common.request_demo') }}
                    </a>
                </div>
            </div>
        </header>

        <welcome-section></welcome-section>

        <features-section></features-section>

        <how-it-works-section></how-it-works-section>

        <section class="w-full bg-white px-6 py-10">
            <div class="container mx-auto flex max-w-2xl flex-col gap-3 rounded-xl border border-ewhite-200 bg-ewhite-50 p-5">
                <label class="flex flex-col gap-2 text-sm font-medium text-eblack-700">
                    {{ $t("marketing.widget_demo.organization_id_label") }}
                    <input v-model.number="widgetOrgIdInput" type="number" class="rounded-lg border border-ewhite-300 bg-white px-3 py-2 text-sm" :placeholder="$t('marketing.widget_demo.organization_id_placeholder')">
                </label>
                <label class="flex flex-col gap-2 text-sm font-medium text-eblack-700">
                    {{ $t("marketing.widget_demo.catalog_grant_label") }}
                    <input v-model.trim="widgetGrantInput" class="rounded-lg border border-ewhite-300 bg-white px-3 py-2 font-mono text-sm" :placeholder="$t('marketing.widget_demo.catalog_grant_placeholder')">
                </label>
                <label class="flex items-center gap-2 text-sm font-medium text-eblack-700">
                    <input v-model="widgetCaptchaRequiredInput" type="checkbox" class="rounded border-ewhite-300">
                    {{ $t("marketing.widget_demo.captcha_required_label") }}
                </label>
                <label v-if="widgetCaptchaRequiredInput" class="flex flex-col gap-2 text-sm font-medium text-eblack-700">
                    {{ $t("marketing.widget_demo.turnstile_site_key_label") }}
                    <input v-model.trim="widgetTurnstileSiteKeyInput" class="rounded-lg border border-ewhite-300 bg-white px-3 py-2 font-mono text-sm" :placeholder="$t('marketing.widget_demo.turnstile_site_key_placeholder')">
                </label>
                <button type="button" class="self-start rounded-lg bg-eorange-500 px-4 py-2 text-sm font-medium text-white" @click="applyWidgetConfig">{{ $t("marketing.widget_demo.view_services") }}</button>
                <p class="text-xs text-eblack-500">{{ $t("marketing.widget_demo.hint", { origin: widgetOrigin }) }}</p>
            </div>
        </section>

        <public-service-listing
            v-if="widgetOrgId && widgetGrant"
            :organization-id="widgetOrgId"
            :catalog-grant="widgetGrant"
            :captcha-required="widgetCaptchaRequired"
            :turnstile-site-key="widgetTurnstileSiteKey"
        ></public-service-listing>

        <pricing-section></pricing-section>

        <testimonials-section></testimonials-section>

        <company-information-section></company-information-section>

        <cta-section id="cta-section"></cta-section>

        <footer-section></footer-section>
    </main>
</template>

<script>
import '../../assets/emanta.scss';
import { initFlowbite } from 'flowbite';
import WelcomeSection from './components/WelcomeSection.vue';
import FeaturesSection from './components/FeaturesSection.vue';
import HowItWorksSection from './components/HowItWorksSection.vue';
import PublicServiceListing from './components/PublicServiceListing.vue';
import PricingSection from './components/PricingSection.vue';
import TestimonialsSection from './components/TestimonialsSection.vue';
import CompanyInformationSection from './components/CompanyInformationSection.vue';
import CtaSection from './components/CtaSection.vue';
import FooterSection from './components/FooterSection.vue';

export default {
    name: 'EmantaSite',
    props: [],
    data() {
        return {
            language:'en',
            language_selected: 'English',
            // In-memory only for the session — an access grant must never be
            // persisted to localStorage/sessionStorage.
            widgetOrgId: null,
            widgetGrant: '',
            widgetCaptchaRequired: false,
            widgetTurnstileSiteKey: '',
            widgetOrgIdInput: null,
            widgetGrantInput: '',
            widgetCaptchaRequiredInput: false,
            widgetTurnstileSiteKeyInput: '',
        }
    },
    components:{
        WelcomeSection,
        FeaturesSection,
        HowItWorksSection,
        PublicServiceListing,
        PricingSection,
        TestimonialsSection,
        CompanyInformationSection,
        CtaSection,
        FooterSection
    },
    methods: {
        changeLanguage(language){
            this.language = language;
            this.$i18n.locale = this.language;
            this.language_selected = language == 'en' ? 'English' : 'Español';
        },
        applyWidgetConfig(){
            this.widgetOrgId = this.widgetOrgIdInput;
            this.widgetGrant = this.widgetGrantInput;
            this.widgetCaptchaRequired = this.widgetCaptchaRequiredInput;
            this.widgetTurnstileSiteKey = this.widgetTurnstileSiteKeyInput;
        }
    },
    computed: {
        widgetOrigin(){ return window.location.origin; }
    },
    mounted() {
        initFlowbite();
    }
}
</script>
